import {
  afterNextRender,
  Component,
  computed,
  ElementRef,
  inject,
  Injector,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Tag } from 'primeng/tag';
import { Textarea } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { QuoteRevision } from '../../../models/quote-revision.model';
import { QuoteService } from '../../quote.service';
import { isPermissionError, parseApiError } from '../../quotes-api-error';

@Component({
  selector: 'app-quote-revisions',
  imports: [
    Button,
    Card,
    CurrencyPipe,
    DatePipe,
    Dialog,
    InputText,
    Tag,
    Textarea,
    TooltipModule,
  ],
  templateUrl: './quote-revisions.component.html',
})
export class QuoteRevisionsComponent {
  quoteService = inject(QuoteService);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  private injector = inject(Injector);

  private nameInput = viewChild<ElementRef<HTMLInputElement>>('nameInput');

  quoteCode = input.required<string>();
  revisions = input.required<QuoteRevision[]>();
  viewingRevisionNumber = input.required<number>();
  currentRevisionId = input.required<string>();
  approvalFlowEnabled = input(false);
  // Creator tier: may publish or request review.
  canSubmit = input(false);
  // Approver tier: may approve or request changes.
  canApprove = input(false);

  readonly maxNameLength = 80;

  isBusy = signal(false);
  editingRevisionId = signal<string | null>(null);
  draftName = signal('');
  requestChangesRevision = signal<QuoteRevision | null>(null);
  requestChangesNotes = signal('');

  displayName(revision: QuoteRevision): string {
    return revision.name || `${this.quoteCode()}-${revision.number}`;
  }

  startEdit(revision: QuoteRevision): void {
    this.draftName.set(this.displayName(revision));
    this.editingRevisionId.set(revision.id);
    afterNextRender(() => this.nameInput()?.nativeElement.select(), {
      injector: this.injector,
    });
  }

  onDraftInput(event: Event): void {
    this.draftName.set((event.target as HTMLInputElement).value);
  }

  cancelEdit(): void {
    this.editingRevisionId.set(null);
  }

  async saveName(revision: QuoteRevision): Promise<void> {
    const name = this.draftName().trim();
    if (name === this.displayName(revision)) {
      this.cancelEdit();
      return;
    }
    const saved = await this.run(
      () => this.quoteService.renameRevision(revision.id, name),
      `Could not rename ${this.displayName(revision)}.`,
    );
    if (saved) {
      this.cancelEdit();
    }
  }

  private currentRevision = computed(() =>
    this.revisions().find(
      (revision) => revision.id === this.currentRevisionId(),
    ),
  );

  hasCurrentDraft = computed(
    () => this.currentRevision()?.published_at === null,
  );

  canDiscardCurrentDraft = computed(() => {
    const current = this.currentRevision();
    return (
      !!current &&
      current.published_at === null &&
      this.revisions().some(
        (revision) =>
          revision.number < current.number && revision.published_at !== null,
      )
    );
  });

  isRestorable(revision: QuoteRevision): boolean {
    return (
      revision.published_at !== null && revision.id !== this.currentRevisionId()
    );
  }

  canPublish(revision: QuoteRevision): boolean {
    return (
      !this.approvalFlowEnabled() &&
      this.canSubmit() &&
      revision.published_at === null
    );
  }

  canRequestReview(revision: QuoteRevision): boolean {
    return (
      this.approvalFlowEnabled() &&
      this.canSubmit() &&
      (revision.approval_status === 'NOT_REQUESTED' ||
        revision.approval_status === 'CHANGES_REQUESTED')
    );
  }

  canReview(revision: QuoteRevision): boolean {
    return (
      this.approvalFlowEnabled() &&
      this.canApprove() &&
      revision.approval_status === 'REQUESTED'
    );
  }

  publish(revision: QuoteRevision): Promise<boolean> {
    return this.run(
      () => this.quoteService.publishRevision(),
      `Could not publish ${this.displayName(revision)}.`,
    );
  }

  requestReview(revision: QuoteRevision): Promise<boolean> {
    return this.run(
      () => this.quoteService.requestReview(),
      `Could not request review for ${this.displayName(revision)}.`,
    );
  }

  approve(revision: QuoteRevision): Promise<boolean> {
    return this.run(
      () => this.quoteService.approveRevision(),
      `Could not approve ${this.displayName(revision)}.`,
    );
  }

  openRequestChanges(revision: QuoteRevision): void {
    this.requestChangesNotes.set('');
    this.requestChangesRevision.set(revision);
  }

  closeRequestChanges(): void {
    this.requestChangesRevision.set(null);
  }

  onRequestChangesInput(event: Event): void {
    this.requestChangesNotes.set((event.target as HTMLTextAreaElement).value);
  }

  async submitRequestChanges(): Promise<void> {
    const revision = this.requestChangesRevision();
    if (!revision) {
      return;
    }
    const sent = await this.run(
      () => this.quoteService.requestChanges(this.requestChangesNotes()),
      `Could not request changes for ${this.displayName(revision)}.`,
    );
    if (sent) {
      this.closeRequestChanges();
    }
  }

  confirmRestore(revision: QuoteRevision): void {
    const label = this.displayName(revision);
    this.confirmationService.confirm({
      header: `Restore ${label}`,
      message: `This creates a new draft revision with the items, prices, and currency from ${label}. Existing revisions are kept.`,
      icon: 'pi pi-replay',
      acceptLabel: 'Restore',
      rejectLabel: 'Cancel',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () =>
        this.run(
          () => this.quoteService.restoreRevision(revision.id),
          `Could not restore ${label}.`,
        ),
    });
  }

  confirmDiscardDraft(revision: QuoteRevision): void {
    const label = this.displayName(revision);
    this.confirmationService.confirm({
      header: `Discard draft ${label}`,
      message: `All changes in draft ${label} will be permanently deleted and the quote will return to its last published revision.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Discard',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () =>
        this.run(
          () => this.quoteService.discardRevision(revision.id),
          `Could not discard ${label}.`,
        ),
    });
  }

  private async run(
    action: () => Promise<unknown>,
    fallbackMessage: string,
  ): Promise<boolean> {
    this.isBusy.set(true);
    try {
      await action();
      return true;
    } catch (error: unknown) {
      if (!isPermissionError(error)) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: parseApiError(error, fallbackMessage),
        });
      }
      return false;
    } finally {
      this.isBusy.set(false);
    }
  }
}
